import type { PdfjsLike } from '../parsers';
import type { ErrorStep } from '../diagnostics';

/**
 * Starting PDF.js's worker, with fallbacks (Samsung Internet, 2026-10-10: "couldn't open the PDF").
 * 1. A module worker (the build's own .mjs worker file).
 * 2. A classic worker: the same worker code bundled as a classic script (some browsers or settings
 *    refuse module workers).
 * 3. The main thread: the worker code imported into the page (PDF.js's "fake worker"), slower, with
 *    nothing else changed: it's a script of this site, so the page CSP (script-src 'self') allows it.
 * Each failure is reported by stage (spawn: the constructor threw; module: the script failed to load or
 * run; timeout: no "ready" within the limit) with the error's type only, never its message.
 */
export type PdfWorkerMode = 'module' | 'classic' | 'main';

export interface WorkerLike {
  addEventListener(type: 'message' | 'error', fn: (e: { data?: unknown; error?: unknown }) => void): void;
  terminate(): void;
}
export type WorkerCtor = new (url: string | URL, options?: { type?: 'module' | 'classic' }) => WorkerLike;

export const WORKER_READY_MS = 10_000;

/** PDF.js's worker says `ready` once its code has run (MessageHandler: { action: 'ready' }). */
export function workerReady(w: WorkerLike, ms = WORKER_READY_MS): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(Object.assign(new Error('no ready message'), { name: 'Timeout', stage: 'workerTimeout' })), ms);
    w.addEventListener('message', (e) => {
      if ((e.data as { action?: unknown } | undefined)?.action === 'ready') {
        clearTimeout(timer);
        resolve();
      }
    });
    w.addEventListener('error', (e) => {
      clearTimeout(timer);
      reject(Object.assign(e.error instanceof Error ? e.error : new Error('worker script failed'), { stage: 'workerModule' }));
    });
  });
}

export interface StartDeps {
  Worker: WorkerCtor | undefined;
  /** The module worker file (the ?url asset). */
  moduleUrl: string;
  /** The classic (iife) build of the same worker code. */
  classicUrl: string | URL;
  /** Imports the worker code into the page (sets globalThis.pdfjsWorker). */
  importOnMainThread: () => Promise<unknown>;
  onError: (stage: ErrorStep, err: unknown) => void;
  readyMs?: number;
}

/** Configures `pdfjs` with the first worker that starts; returns which one. */
export async function startPdfWorker(pdfjs: PdfjsLike & { GlobalWorkerOptions: { workerPort?: unknown; workerSrc?: string } }, deps: StartDeps): Promise<PdfWorkerMode> {
  const attempt = async (url: string | URL, type: 'module' | 'classic'): Promise<WorkerLike | undefined> => {
    if (!deps.Worker) return undefined;
    let w: WorkerLike;
    try {
      w = new deps.Worker(url, { type });
    } catch (err) {
      deps.onError('workerSpawn', err);
      return undefined;
    }
    try {
      await workerReady(w, deps.readyMs);
      return w;
    } catch (err) {
      deps.onError((err as { stage?: ErrorStep }).stage ?? 'workerModule', err);
      w.terminate();
      return undefined;
    }
  };
  pdfjs.GlobalWorkerOptions.workerSrc = deps.moduleUrl;
  for (const [url, type] of [[deps.moduleUrl, 'module'], [deps.classicUrl, 'classic']] as const) {
    const w = await attempt(url, type);
    if (w) {
      pdfjs.GlobalWorkerOptions.workerPort = w;
      return type;
    }
  }
  // No worker at all: PDF.js runs its worker code in the page.
  pdfjs.GlobalWorkerOptions.workerPort = null;
  await deps.importOnMainThread();
  return 'main';
}

/**
 * Whether a PDF is encrypted, from its bytes (an /Encrypt entry in the trailer or the cross-reference
 * stream's dictionary, which are never compressed). Lets the app ask for the password before the reader
 * starts, so the prompt appears even where the reader then fails.
 */
export function hasEncryptEntry(bytes: Uint8Array): boolean {
  const needle = [0x2f, 0x45, 0x6e, 0x63, 0x72, 0x79, 0x70, 0x74]; // "/Encrypt"
  outer: for (let i = 0; i + needle.length <= bytes.length; i++) {
    for (let j = 0; j < needle.length; j++) if (bytes[i + j] !== needle[j]) continue outer;
    return true;
  }
  return false;
}
